import { AxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';

import { ApiResponse } from 'types';
import useModal from 'hooks/useModal';
import createCopyProject from 'api/project/createCopyProject';
import { projectCopySchema } from 'schemas/projectCopySchema';

interface FormValues {
  copyProjectName: string;
}

interface UseProjectCopyParams {
  projectName: string;
  ownerName: string;
  projectsRefetch: () => void;
}

const useProjectCopy = ({ projectName, ownerName, projectsRefetch }: UseProjectCopyParams) => {
  const modal = useModal();
  const useFormReturn = useForm<FormValues>({
    resolver: yupResolver(projectCopySchema),
    defaultValues: {
      copyProjectName: projectName,
    },
  });

  const projectCopyMutation = useMutation({
    mutationFn: createCopyProject,
    onSuccess: () => {
      projectsRefetch();
      modal.close();
    },
    onError: (error: AxiosError<ApiResponse>) => {
      if (error?.response?.data?.errorCode === 21001) {
        return useFormReturn.setError('copyProjectName', {
          message: '이미 존재하는 프로젝트 이름입니다.',
        });
      }
      toast.error('프로젝트 복제에 실패했습니다.');
    },
  });

  const onValid = (values: FormValues) => {
    projectCopyMutation.mutate({
      projectName,
      ownerName,
      copyProjectName: values.copyProjectName,
    });
  };

  return {
    isLoading: projectCopyMutation.isPending,
    ...useFormReturn,
    onValid,
  };
};

export default useProjectCopy;
